<script setup>
import { onMounted, ref } from 'vue'
import { auth, database } from '@/firebase'
import {isAuthenticated} from "@/utils/auth";
import setNavPills from "@/assets/js/nav-pills";
import setTooltip from "@/assets/js/tooltip";
import ArgonAlert from "../../components/ArgonAlert.vue";
import {onValue, ref as dbRef, remove, set} from "firebase/database";

const searchTerm = ref('')
const notFound = ref(false)
const loading = ref(false)
let wordData = ref(null);
const favoritesWords = ref([]);


const alertMessage = ref('');
const alertColor = ref('success');
const showAlert = ref(false);

const search = async () => {
  wordData.value = ''
  const word = searchTerm.value.trim().toLowerCase()

  if (!word) {
    notFound.value = false
    return
  }

  loading.value = true
  notFound.value = false

  try {
    const res = await fetch(`https://api.dictionaryapi.dev/api/v2/entries/en/${word}`)
    if (!res.ok) throw new Error('Không tìm thấy từ')

    const data = await res.json()
    wordData.value = data[0];
  } catch (err) {
    notFound.value = true
  } finally {
    loading.value = false
  }
}
const displayAlert = (message, color = 'success', duration = 3000) => {
  alertMessage.value = message;
  alertColor.value = color;
  showAlert.value = true;
  if (duration > 0) {
    setTimeout(() => {
      showAlert.value = false;
    }, duration);
  }
};
const loadFavorites = () => {
  const uid = auth.currentUser?.uid;
  if (!isAuthenticated()) {
    displayAlert("Bạn cần đăng nhập để xem danh sách yêu thích.", "danger", 5000);
    return;
  }
  const userFlashcardsRef = dbRef(database, `users/${uid}/favorites`);
  onValue(userFlashcardsRef, (snapshot) => {
    const data = snapshot.val();
    favoritesWords.value = data ? Object.keys(data) : [];
  }, (error) => {
    console.error("Lỗi khi tải:", error);
    displayAlert("Không thể tải danh sách yêu thích.", "danger", 0);
  });
};
const isFavorite = (word) => favoritesWords.value.includes(word);

const toggleFavorite = async (wordObj) => {
  const uid = auth.currentUser?.uid;
  if (!isAuthenticated()) {
    displayAlert("Bạn cần đăng nhập để thêm.", "danger", 5000);
    return;
  }

  const word = wordObj.word;
  const wordRef = dbRef(database, `users/${uid}/favorites/${word}`);

  if (isFavorite(word)) {
    await remove(wordRef);
    displayAlert("Đã xoá khỏi yêu thích", "info", 3000);
  } else {
    await set(wordRef, wordObj);
    displayAlert("Đã thêm vào yêu thích", "success", 3000);
  }
};

onMounted(() => {
  setNavPills();
  setTooltip();
  if(isAuthenticated()) {
    loadFavorites();
  }
});
</script>

<template>
  <div class="pe-md-3 d-flex align-items-center mb-3 ms-md-auto">
    <div class="input-group">
      <span class="input-group-text" @click="search" style="cursor: pointer;">
        <i class="fas fa-search"></i>
      </span>
      <input v-model="searchTerm" @keyup.enter="search" type="text" class="form-control"
        placeholder="Nhập từ tiếng Anh..." />
    </div>
  </div>
  <div>
    <div class="card">
      <div class="card-body">
        <div class="row gx-4">
          <p v-if="notFound" class="text-danger">"{{ searchTerm }}" Không tìm thấy</p>
          <div class="col-lg-4 col-md-6 my-sm-auto ms-sm-auto me-sm-0">
            <div class="nav-wrapper position-relative end-0">
              <ul class="p-1 bg-transparent nav nav-pills nav-fill" role="tablist">
                <li class="nav-item">
                  <a class="mx-auto px-0 py-1 mb-0 nav-link active" data-bs-toggle="tab" href="#phonetics" role="tab"
                    aria-selected="true">
                    <svg class="text-dark" width="16px" height="16px" viewBox="0 0 42 42" version="1.1"
                      xmlns="http://www.w3.org/2000/svg" xmlns:xlink="http://www.w3.org/1999/xlink">
                      <g stroke="none" stroke-width="1" fill="none" fill-rule="evenodd">
                        <g transform="translate(-2319.000000, -291.000000)" fill="#FFFFFF" fill-rule="nonzero">
                          <g transform="translate(1716.000000, 291.000000)">
                            <g transform="translate(603.000000, 0.000000)">
                              <path class="color-background"
                                d="M22.7597136,19.3090182 L38.8987031,11.2395234 C39.3926816,10.9925342 39.592906,10.3918611 39.3459167,9.89788265 C39.249157,9.70436312 39.0922432,9.5474453 38.8987261,9.45068056 L20.2741875,0.1378125 L20.2741875,0.1378125 C19.905375,-0.04725 19.469625,-0.04725 19.0995,0.1378125 L3.1011696,8.13815822 C2.60720568,8.38517662 2.40701679,8.98586148 2.6540352,9.4798254 C2.75080129,9.67332903 2.90771305,9.83023153 3.10122239,9.9269862 L21.8652864,19.3090182 C22.1468139,19.4497819 22.4781861,19.4497819 22.7597136,19.3090182 Z" />
                              <path class="color-background"
                                d="M23.625,22.429159 L23.625,39.8805372 C23.625,40.4328219 24.0727153,40.8805372 24.625,40.8805372 C24.7802551,40.8805372 24.9333778,40.8443874 25.0722402,40.7749511 L41.2741875,32.673375 L41.2741875,32.673375 C41.719125,32.4515625 42,31.9974375 42,31.5 L42,14.241659 C42,13.6893742 41.5522847,13.241659 41,13.241659 C40.8447549,13.241659 40.6916418,13.2778041 40.5527864,13.3472318 L24.1777864,21.5347318 C23.8390024,21.7041238 23.625,22.0503869 23.625,22.429159 Z"
                                opacity="0.7" />
                              <path class="color-background"
                                d="M20.4472136,21.5347318 L1.4472136,12.0347318 C0.953235098,11.7877425 0.352562058,11.9879669 0.105572809,12.4819454 C0.0361450918,12.6208008 6.47121774e-16,12.7739139 0,12.929159 L0,30.1875 L0,30.1875 C0,30.6849375 0.280875,31.1390625 0.7258125,31.3621875 L19.5528096,40.7750766 C20.0467945,41.0220531 20.6474623,40.8218132 20.8944388,40.3278283 C20.963859,40.1889789 21,40.0358742 21,39.8806379 L21,22.429159 C21,22.0503869 20.7859976,21.7041238 20.4472136,21.5347318 Z"
                                opacity="0.7" />
                            </g>
                          </g>
                        </g>
                      </g>
                    </svg>
                    <span class="ms-1">Ngữ âm</span>
                  </a>
                </li>
                <li class="nav-item">
                  <a class="px-0 py-1 mb-0 nav-link" data-bs-toggle="tab" href="#meanings" role="tab"
                    aria-selected="false">
                    <svg class="text-dark" width="16px" height="16px" viewBox="0 0 40 44" version="1.1"
                      xmlns="http://www.w3.org/2000/svg" xmlns:xlink="http://www.w3.org/1999/xlink">
                      <title>document</title>
                      <g stroke="none" stroke-width="1" fill="none" fill-rule="evenodd">
                        <g transform="translate(-1870.000000, -591.000000)" fill="#FFFFFF" fill-rule="nonzero">
                          <g transform="translate(1716.000000, 291.000000)">
                            <g transform="translate(154.000000, 300.000000)">
                              <path class="color-background"
                                d="M40,40 L36.3636364,40 L36.3636364,3.63636364 L5.45454545,3.63636364 L5.45454545,0 L38.1818182,0 C39.1854545,0 40,0.814545455 40,1.81818182 L40,40 Z"
                                opacity="0.603585379" />
                              <path class="color-background"
                                d="M30.9090909,7.27272727 L1.81818182,7.27272727 C0.814545455,7.27272727 0,8.08727273 0,9.09090909 L0,41.8181818 C0,42.8218182 0.814545455,43.6363636 1.81818182,43.6363636 L30.9090909,43.6363636 C31.9127273,43.6363636 32.7272727,42.8218182 32.7272727,41.8181818 L32.7272727,9.09090909 C32.7272727,8.08727273 31.9127273,7.27272727 30.9090909,7.27272727 Z M18.1818182,34.5454545 L7.27272727,34.5454545 L7.27272727,30.9090909 L18.1818182,30.9090909 L18.1818182,34.5454545 Z M25.4545455,27.2727273 L7.27272727,27.2727273 L7.27272727,23.6363636 L25.4545455,23.6363636 L25.4545455,27.2727273 Z M25.4545455,20 L7.27272727,20 L7.27272727,16.3636364 L25.4545455,16.3636364 L25.4545455,20 Z" />
                            </g>
                          </g>
                        </g>
                      </g>
                    </svg>
                    <span class="ms-1">Ý nghĩa</span>
                  </a>
                </li>
                <li class="nav-item" v-if="wordData">
                  <button class="px-0 py-1 mb-0 nav-link" data-bs-toggle="tab" href="#source" role="tab"
                    aria-selected="false"  @click="toggleFavorite(wordData)">
                    <span class="ms-1" v-if="isFavorite(wordData.word)">❤️</span>
                    <span class="ms-1" v-else>🤍</span>
                  </button>
                </li>
              </ul>
            </div>
          </div>

          <div class="tab-content mt-3">
            <div class="tab-pane fade show active" id="phonetics" role="tabpanel">
              <div v-if="wordData" class="max-w-3xl mx-auto p-4">
                <!-- PHONETICS -->
                <div class="mb-3">
                  <h3 class="text-xl font-semibold mb-2">Ngữ âm</h3>
                  <div v-for="(phonetic, index) in wordData.phonetics" :key="index" class="mb-2">
                    <p v-if="phonetic.text" class="text-gray-700 italic">{{ phonetic.text }}</p>
                    <audio v-if="phonetic.audio" :src="phonetic.audio" controls class="mt-1" />
                  </div>
                </div>
              </div>
            </div>
            <div class="tab-pane fade" id="meanings" role="tabpanel">
              <div v-if="wordData" class="max-w-3xl mx-auto p-4">
                <!-- MEANINGS -->
                <div>
                  <h2 class="text-xl font-semibold mb-2">Ý nghĩa</h2>
                  <div v-for="(meaning, mIndex) in wordData.meanings" :key="mIndex" class="mb-4">
                    <h3 class="font-bold text-indigo-600">{{ meaning.partOfSpeech }}</h3>
                    <ul class="list-disc list-inside ml-2">
                      <li v-for="(def, dIndex) in meaning.definitions" :key="dIndex" class="mb-2">
                        <p>{{ def.definition }}</p>
                        <p v-if="def.example" class="text-sm text-gray-500">Example: "{{ def.example }}"</p>
                      </li>
                    </ul>

                    <div v-if="meaning.synonyms.length" class="mt-1 text-sm text-green-700">
                      Synonyms: {{ meaning.synonyms.join(', ') }}
                    </div>
                    <div v-if="meaning.antonyms.length" class="mt-1 text-sm text-red-700">
                      Antonyms: {{ meaning.antonyms.join(', ') }}
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div class="tab-pane fade" id="source" role="tabpanel">
              <div v-if="wordData" class="max-w-3xl mx-auto p-4">
                <!-- SOURCE -->
                <div class="text-sm mt-2">
                  <div v-if="showAlert" class="mb-4">
                    <ArgonAlert :color="alertColor" dismissible>
                      {{ alertMessage }}
                    </ArgonAlert>
                  </div>
                  <p>
                    Nguồn:
                    <a :href="wordData.sourceUrls[0]" target="_blank" class="text-blue-600 underline">
                      {{ wordData.sourceUrls[0] }}
                    </a>
                  </p>
                </div>
              </div>
            </div>
          </div>

        </div>
      </div>
    </div>
  </div>
</template>
